// the send WAITS for the history read to settle (else a click that beats the fetch skips the
// warning entirely) — a FAILED read still only costs the warning, it never blocks 2. arming starts
// a short cooldown so the second click of a double-click can't blow through the confirm before the
// label can be read 3.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { JdSendButton } from "../features/jd-send-button/JdSendButton";
import { JdPriorSends } from "../features/jd-prior-sends/JdPriorSends";
import { JdSendGates } from "../features/jd-send-gates/JdSendGates";
import { api } from "../api";
import { buildJdRxZips, type SpreadFieldExport } from "../lib/spreadExport";
import { chosenJdLink, gateAll } from "../lib/jdSendGate";
import { jdSendRows } from "../lib/jdSendRows";
import {
  classifySendError,
  classifySendResult,
  type JdSendOutcome,
} from "../lib/jdSendOutcome";
import { JdSendOutcomeLine } from "../features/jd-send-outcome/JdSendOutcomeLine";
import { JdLinkModal } from "./JdLinkModal";
import { JdReconsentNote } from "./JdReconsentNote";
import type { DashboardField, JdFieldSend } from "../types/contract";

type SendableExport = SpreadFieldExport & { fieldId: string; rxId?: string };

/* * The JSONB `detail` of a history row — the JD file id rides HERE, not a top-level column (JD14). */
function jdFileIdOf(s: JdFieldSend): string | null {
  const d = s.detail;
  if (d && typeof d === "object") {
    const v = (d as Record<string, unknown>).jd_file_id;
    if (typeof v === "string" && v) return v;
  }
  return null;
}

/* * How long the armed button stays un-clickable — long enough to absorb any double-click. */
const ARM_COOLDOWN_MS = 800;

export function JdOpsSend({
  fields,
  exports,
  growerName,
  ourProduct,
  disabled,
  onSendingChange,
}: {
  /* * the dashboard rows for the selection — the link gate reads `jd_links` off these */
  fields: DashboardField[];
  /* * the SAME exports the download zip is built from, stamped with our field ids */
  exports: SendableExport[];
  growerName: string;
  /* * what's being spread, the product-mapping vocabulary word ('Lime', 'Muriate of Potash', …) */
  ourProduct: string;
  disabled?: boolean;
  /*
   *
   * shells MUST lock their close routes while true — a closed surface must not upload headless.
   */
  onSendingChange?: (sending: boolean) => void;
}) {
  const [linking, setLinking] = useState<DashboardField | null>(null);
  // ambiguous fields (a combined field carries a link from each half): the user's pick, by field id
  const [chosen, setChosen] = useState<Record<string, string>>({}); // fieldId -> `${jd_org_id}:${jd_field_id}`
  const [armed, setArmed] = useState(false);
  const [armCooldown, setArmCooldown] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcomes, setOutcomes] = useState<
    Record<string, JdSendOutcome | { kind: "sending" }>
  >({});
  // SERVER history (JD14) and LOCAL just-sent rows are held apart: a refetch replaces the server
  // list wholesale, and a sent-but-UNRECORDED send exists ONLY locally — merging them in one map let
  // the refetch erase exactly the row whose whole point is surviving the server's amnesia.
  const [priorSends, setPriorSends] = useState<Record<string, JdFieldSend[]>>(
    {},
  );
  const [localSends, setLocalSends] = useState<Record<string, JdFieldSend[]>>(
    {},
  );
  const [settled, setSettled] = useState<Record<string, boolean>>({});
  // JD field NAMES for the ambiguous chooser (best-effort; ids are what no user knows their fields by)
  const [jdNames, setJdNames] = useState<Record<string, string>>({});
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const rows = jdSendRows(fields, exports);
  const gated = gateAll(rows.map((r) => r.field));
  const unlinked = gated.filter((g) => g.gate.kind === "unlinked");
  const ambiguous = gated.filter((g) => g.gate.kind === "ambiguous");

  // the link each sendable field will use: the single link, or the user's explicit pick
  const linkFor = (field: DashboardField) =>
    chosenJdLink(
      gated.find((g) => g.field.field_id === field.field_id)?.gate,
      field.field_id,
      chosen,
    );
  const resolved = rows.map((r) => ({ ...r, link: linkFor(r.field) }));
  const allResolved = rows.length > 0 && resolved.every((r) => r.link);

  // Refetches replace the SERVER list only (never localSends).
  const loadedFor = useRef<string>("");
  const idsKey = rows
    .map((r) => r.field.field_id)
    .sort()
    .join(",");
  useEffect(() => {
    if (!idsKey || loadedFor.current === idsKey) return;
    loadedFor.current = idsKey;
    for (const id of idsKey.split(",")) {
      api.jdFieldSends(id).then(
        (sends) => {
          setPriorSends((p) => ({ ...p, [id]: sends }));
          setSettled((s) => ({ ...s, [id]: true }));
        },
        () => setSettled((s) => ({ ...s, [id]: true })),
      );
    }
  }, [idsKey]);
  const historyReady = rows.every((r) => settled[r.field.field_id]);

  // best-effort names for the ambiguous chooser (the linking flow shows names for the same reason)
  const namesFor = useRef<string>("");
  const ambiguousKey = ambiguous.map((g) => g.field.field_id).join(",");
  const ambiguousRef = useRef(ambiguous);
  useLayoutEffect(() => {
    ambiguousRef.current = ambiguous;
  }, [ambiguous]);
  useEffect(() => {
    const ambiguous = ambiguousRef.current;
    if (!ambiguous.length) return;
    const orgs = [
      ...new Set(
        ambiguous.flatMap((g) =>
          g.gate.kind === "ambiguous"
            ? g.gate.links.map((l) => l.jd_org_id)
            : [],
        ),
      ),
    ].sort();
    const k = orgs.join(",");
    if (!k || namesFor.current === k) return;
    namesFor.current = k;
    for (const org of orgs) {
      api.jdFields(org).then(
        (list) =>
          setJdNames((n) => ({
            ...n,
            ...Object.fromEntries(
              list.map((f) => [`${f.jd_org_id}:${f.jd_field_id}`, f.name]),
            ),
          })),
        () => {},
      );
    }
  }, [ambiguousKey]);

  // Never mid-send: the loop's own writes must land.
  const payloadSig =
    rows
      .map(
        (r) =>
          `${r.field.field_id}:${r.export.rxId ?? ""}:${r.export.products.map((p) => p.column).join("+")}`,
      )
      .join("|") + `|${JSON.stringify(chosen)}|${ourProduct}`;
  const sigRef = useRef(payloadSig);
  useEffect(() => {
    if (sigRef.current === payloadSig) return;
    sigRef.current = payloadSig;
    if (!sending) {
      setArmed(false);
      setOutcomes({});
    }
  }, [payloadSig, sending]);

  const sendsOf = (fid: string): JdFieldSend[] => [
    ...(localSends[fid] ?? []),
    ...(priorSends[fid] ?? []),
  ];
  const warned = rows.filter((r) =>
    sendsOf(r.field.field_id).some((s) => s.our_product === ourProduct),
  );

  const send = async () => {
    if (disabled || sending || !allResolved || !historyReady || armCooldown)
      return;
    // the ARM step: a warned re-send takes a second, explicit click — after a cooldown that outlives
    // any double-click (review rule 2)
    if (warned.length > 0 && !armed) {
      setArmed(true);
      setArmCooldown(true);
      window.setTimeout(() => setArmCooldown(false), ARM_COOLDOWN_MS);
      return;
    }
    setArmed(false);
    setSending(true);
    onSendingChange?.(true);
    try {
      for (const r of resolved) {
        // the unmount belt: shells lock their close routes while sending, but if this component is
        // ever unmounted anyway, NOT-YET-STARTED uploads stop here — a surface that is gone must
        // not keep writing to a grower's Ops Center invisibly.
        if (!aliveRef.current) break;
        if (!r.link) continue;
        const fid = r.field.field_id;
        setOutcomes((o) => ({ ...o, [fid]: { kind: "sending" } }));
        try {
          const zips = await buildJdRxZips({
            fields: [r.export],
            growerName,
            now: new Date(),
          });
          const z = zips[0];
          if (!z) {
            setOutcomes((o) => ({
              ...o,
              [fid]: {
                kind: "failed",
                message: "No cells with rates to send.",
              },
            }));
            continue;
          }
          const res = await api.sendJdSpreadMap(
            fid,
            {
              jdOrgId: r.link.jd_org_id,
              jdFieldId: r.link.jd_field_id,
              ourProduct,
              filename: z.name,
              rxId: r.export.rxId,
            },
            z.data,
          );
          setOutcomes((o) => ({ ...o, [fid]: classifySendResult(res) }));
          // a completed send IS a prior send now — held LOCALLY so a history refetch (which for an
          // unrecorded send knows nothing) cannot erase the warning it must produce
          setLocalSends((p) => ({
            ...p,
            [fid]: [
              {
                our_product: ourProduct,
                jd_org_id: res.jd_org_id,
                jd_field_id: res.jd_field_id,
                outcome: "sent",
                sent_at: new Date().toISOString(),
                detail: { jd_file_id: res.jd_file_id },
              },
              ...(p[fid] ?? []),
            ],
          }));
        } catch (e) {
          setOutcomes((o) => ({ ...o, [fid]: classifySendError(e) }));
        }
      }
    } finally {
      setSending(false);
      onSendingChange?.(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* A connection that predates a scope change still looks connected and still 403s — say so first. */}
      <JdReconsentNote />

      <JdSendGates
        unlinked={unlinked}
        ambiguous={ambiguous}
        chosen={chosen}
        jdNames={jdNames}
        onLink={setLinking}
        onChoose={(fieldId, value) =>
          setChosen((c) => ({ ...c, [fieldId]: value }))
        }
      />

      <JdPriorSends
        items={warned.map((r) => {
          const prior = sendsOf(r.field.field_id).find(
            (s) => s.our_product === ourProduct,
          )!;
          return {
            fieldId: r.field.field_id,
            fieldName: r.field.field_name,
            product: ourProduct,
            sentAt: prior.sent_at,
            fileId: jdFileIdOf(prior),
          };
        })}
      />

      {rows.map((r) => (
        <div key={r.field.field_id} className="contents">
          <JdSendOutcomeLine
            fieldName={r.field.field_name}
            outcome={outcomes[r.field.field_id]}
          />
        </div>
      ))}

      <JdSendButton
        sending={sending}
        armed={armed}
        disabled={
          !!disabled || sending || !allResolved || !historyReady || armCooldown
        }
        title={
          allResolved
            ? historyReady
              ? undefined
              : "Checking this field’s send history…"
            : rows.length === 0
              ? "Nothing ready to send"
              : "Connect or choose a John Deere field first"
        }
        onSend={() => void send()}
      />

      {linking && (
        <JdLinkModal
          fieldId={linking.field_id}
          onClose={() => setLinking(null)}
        />
      )}
    </div>
  );
}
